import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchResumoDeConsumo, ResumoDeConsumo } from '../../../api/consumoIaApi';
import {
  EstadoCotacaoOnline,
  fetchConsumoCotacaoOnline,
  fetchEstadoCotacaoOnline,
  ORCAMENTO_CILIA_HABILITADO,
  ResumoConsumoCotacaoOnline,
} from '../../../api/orcamentoCiliaApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { notify } from '../../../shared/feedback/notify';
import AbaCotacaoOnline from '../components/consumo-ia/AbaCotacaoOnline';
import AbaRadarAssistente, { CardDeTabela, CartaoDeTotais } from '../components/consumo-ia/AbaRadarAssistente';
import { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { fmtMes, fmtUsd, nomeDoUsuario, SEM_USUARIO_EXPLICACAO } from '../components/consumo-ia/formatacao';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto de página compartilhado — import explícito, nunca depender de CSS carregado por
// outra tela (lição do BUG-V74, at-67).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';

/**
 * Consumo de IA (rota `/admin/sistema/consumo-ia`) — at-12; em abas desde 20/10/2026.
 *
 * Pedido do Victor (20/10/2026): não só o consumo somado, mas **quem utilizou, para quê e quando**.
 * Três abas: **Total** (as aplicações somadas), **Radar e Assistente** (o que o SOAC grava) e
 * **Cotação online** (o que o backend de Requisições grava, uma linha por pesquisa).
 *
 * ⚠️ O custo exibido é ESTIMADO pela tabela de preços local; o número oficial é o do Console
 * da Anthropic. A tela diz isso em hint fixa, não só no drawer.
 *
 * 🔴 O Total só soma o que respondeu: se uma das aplicações não carregar, a aba Total mostra a
 * faixa de erro em vez de um total que pareceria completo e não é (regra da at-60).
 */

type Aba = 'TOTAL' | 'RADAR' | 'COTACAO';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que esta tela mede',
    conteudo: (
      <p>
        Cada vez que o sistema usa inteligência artificial, ele registra quem pediu, para quê,
        quando e o tamanho da operação. Os pontos que usam IA hoje são o{' '}
        <strong>Assistente do Dashboard Oficina</strong>, os <strong>Radares</strong> (a redação
        dos cartões e os achados do assistente) e a <strong>cotação online de peças</strong> da tela
        de Requisições. O conteúdo das perguntas e das respostas nunca é gravado.
      </p>
    ),
  },
  {
    titulo: 'As três abas',
    conteudo: (
      <>
        <p>
          <strong>Total</strong> soma as aplicações e mostra quanto cada uma e cada pessoa
          consumiu. Se alguma aplicação não responder, o total não é exibido, porque seria menor que
          o real sem avisar.
        </p>
        <p>
          <strong>Radar e Assistente</strong> e <strong>Cotação online</strong> trazem o detalhe de
          cada uma, com a lista de cada uso: quem, para quê e quando.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem pediu',
    conteudo: (
      <p>
        <strong>Sistema (sem usuário)</strong> é o que roda sozinho: a rotina diária do Radar e a
        redação dos cartões. Também aparecem assim os registros anteriores a 20/10/2026, quando o
        sistema passou a guardar quem pediu. Na cotação online <strong>automática</strong>, quem
        aparece é quem concluiu a requisição, porque é esse clique que dispara a pesquisa.
      </p>
    ),
  },
  {
    titulo: 'O custo é uma estimativa',
    conteudo: (
      <p>
        O valor em dólares é calculado aqui, multiplicando os tokens (e, na cotação online, as
        buscas na internet) pela tabela de preços pública da Anthropic. Serve para acompanhar a
        ordem de grandeza — o número oficial da fatura é sempre o do{' '}
        <strong>Console da Anthropic</strong>. Modelo fora da tabela local fica fora da soma, e a
        tela avisa.
      </p>
    ),
  },
  {
    titulo: 'O que é o cache',
    conteudo: (
      <p>
        Parte do que o sistema envia ao modelo se repete a cada chamada (as instruções fixas). Essa
        parte é guardada em cache pela Anthropic: <strong>ler do cache</strong> custa cerca de um
        décimo do preço normal de entrada. Um volume grande de "cache lido" é economia, não gasto a
        cortar.
      </p>
    ),
  },
  {
    titulo: 'Janela e fuso',
    conteudo: (
      <p>
        Os cartões e as tabelas por usuário cobrem os <strong>últimos 30 dias</strong>, com os dias
        contados no fuso da oficina. As tabelas <strong>por mês</strong> respondem "quanto custou o
        mês passado", que a janela de 30 dias não responde por atravessar a virada do mês. Mês sem
        nenhum uso não aparece: uma linha zerada seria indistinguível de um mês em que o registro
        falhou.
      </p>
    ),
  },
];

/** Mesmo usuário nas duas aplicações: o id é o do SOAC (as duas leem o mesmo login). */
function juntarPorUsuario(
  soac: ResumoDeConsumo,
  cotacao: ResumoConsumoCotacaoOnline | null,
): Array<{ chave: string; usuarioId: string | null; nome: string | null; radar: number; cotacao: number }> {
  const mapa = new Map<string, { chave: string; usuarioId: string | null; nome: string | null; radar: number; cotacao: number }>();
  const pegar = (usuarioId: string | null, nome: string | null) => {
    const chave = usuarioId ?? '';
    const atual = mapa.get(chave) ?? { chave, usuarioId, nome: null, radar: 0, cotacao: 0 };
    atual.nome = atual.nome ?? nome;
    mapa.set(chave, atual);
    return atual;
  };
  for (const u of soac.porUsuario ?? []) pegar(u.usuarioId, u.nome).radar += u.custoEstimadoUsd;
  for (const u of cotacao?.porUsuario ?? []) pegar(u.usuarioId, u.nome).cotacao += u.custoUsd;
  return Array.from(mapa.values()).sort((a, b) => b.radar + b.cotacao - (a.radar + a.cotacao));
}

type LinhaDeUsuario = ReturnType<typeof juntarPorUsuario>[number];
type LinhaDeMes = { mes: string; radar: number; cotacao: number };

const AbaTotal: React.FC<{ soac: ResumoDeConsumo; cotacao: ResumoConsumoCotacaoOnline | null }> = ({
  soac,
  cotacao,
}) => {
  const periodos = [
    { titulo: 'Hoje', radar: soac.hoje.custoEstimadoUsd, cot: cotacao?.hoje.custoUsd ?? 0 },
    { titulo: 'Mês corrente', radar: soac.mesCorrente.custoEstimadoUsd, cot: cotacao?.mesCorrente.custoUsd ?? 0 },
    { titulo: `Últimos ${soac.janelaDias} dias`, radar: soac.ultimos30Dias.custoEstimadoUsd, cot: cotacao?.ultimos30Dias.custoUsd ?? 0 },
  ];
  const meses = useMemo(() => {
    const mapa = new Map<string, { radar: number; cotacao: number }>();
    for (const m of soac.porMesEOrigem) mapa.set(m.mes, { radar: m.total.custoEstimadoUsd, cotacao: 0 });
    for (const m of cotacao?.porMes ?? []) {
      const atual = mapa.get(m.mes) ?? { radar: 0, cotacao: 0 };
      atual.cotacao = m.custoUsd;
      mapa.set(m.mes, atual);
    }
    return Array.from(mapa.entries())
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([mes, v]) => ({ mes, ...v }));
  }, [soac, cotacao]);
  const usuarios = useMemo(() => juntarPorUsuario(soac, cotacao), [soac, cotacao]);

  // A coluna da cotação só existe quando o módulo está ligado neste ambiente.
  const colunasPorUsuario: ColunaTabela<LinhaDeUsuario>[] = [
    {
      titulo: 'Usuário',
      largura: 180,
      larguraMaxima: 240,
      texto: (u) => nomeDoUsuario(u.usuarioId, u.nome),
      render: (u) => (
        <span title={u.usuarioId ? undefined : SEM_USUARIO_EXPLICACAO}>{nomeDoUsuario(u.usuarioId, u.nome)}</span>
      ),
    },
    { titulo: 'Radar e Assistente', largura: 150, texto: (u) => fmtUsd(u.radar) },
    ...(cotacao ? [{ titulo: 'Cotação online', largura: 130, texto: (u: LinhaDeUsuario) => fmtUsd(u.cotacao) }] : []),
    { titulo: 'Total', largura: 120, texto: (u) => fmtUsd(u.radar + u.cotacao) },
  ];
  const colunasPorMes: ColunaTabela<LinhaDeMes>[] = [
    { titulo: 'Mês', largura: 150, texto: (m) => fmtMes(m.mes) },
    { titulo: 'Radar e Assistente', largura: 150, texto: (m) => fmtUsd(m.radar) },
    ...(cotacao ? [{ titulo: 'Cotação online', largura: 130, texto: (m: LinhaDeMes) => fmtUsd(m.cotacao) }] : []),
    { titulo: 'Total', largura: 120, texto: (m) => fmtUsd(m.radar + m.cotacao) },
  ];

  const detalhe = (radar: number, cot: number) =>
    cotacao ? `Radar e Assistente ${fmtUsd(radar)} · Cotação online ${fmtUsd(cot)}` : `Radar e Assistente ${fmtUsd(radar)}`;

  return (
    <>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
        {periodos.map((p) => (
          <CartaoDeTotais key={p.titulo} titulo={p.titulo} custo={p.radar + p.cot} detalhe={detalhe(p.radar, p.cot)} />
        ))}
      </div>

      {usuarios.length === 0 ? (
        <div className="admin-card" style={{ marginTop: 16 }}>
          <div className="admin-card-title">Por usuário (últimos {soac.janelaDias} dias)</div>
          <p className="admin-vazio">Ninguém usou inteligência artificial nos últimos {soac.janelaDias} dias.</p>
        </div>
      ) : (
        <CardDeTabela
          titulo={`Por usuário (últimos ${soac.janelaDias} dias)`}
          colunas={colunasPorUsuario}
          itens={usuarios}
          chave={(u) => u.chave}
          vazio=""
        />
      )}

      {meses.length > 0 && (
        <CardDeTabela titulo="Por mês e aplicação" colunas={colunasPorMes} itens={meses} chave={(m) => m.mes} vazio="" />
      )}
    </>
  );
};

const ConsumoIaPage: React.FC = () => {
  const [aba, setAba] = useState<Aba>('TOTAL');
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [resumo, setResumo] = useState<ResumoDeConsumo | null>(null);
  const [erroSoac, setErroSoac] = useState(false);
  const [cotacao, setCotacao] = useState<ResumoConsumoCotacaoOnline | null>(null);
  const [estadoCotacao, setEstadoCotacao] = useState<EstadoCotacaoOnline | null>(null);
  const [erroCotacao, setErroCotacao] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const soac = fetchResumoDeConsumo()
      .then((dados) => {
        setResumo(dados);
        setErroSoac(false);
      })
      .catch((error) => {
        // "Consumo zero" e "não consegui perguntar" são leituras opostas (at-60).
        setResumo(null);
        setErroSoac(true);
        notify.errorFrom(error, 'Não foi possível carregar o consumo de IA. Recarregue a página; se persistir, acione a TI.');
      });
    const cilia = ORCAMENTO_CILIA_HABILITADO
      ? Promise.all([fetchConsumoCotacaoOnline(), fetchEstadoCotacaoOnline().catch(() => null)])
          .then(([dados, estado]) => {
            setCotacao(dados);
            setEstadoCotacao(estado);
            setErroCotacao(false);
          })
          .catch(() => {
            setCotacao(null);
            setErroCotacao(true);
          })
      : Promise.resolve();
    await Promise.all([soac, cilia]);
    setCarregando(false);
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const erroDaCotacao = (
    <p className="erro-de-tela">
      Não foi possível carregar o consumo da cotação online. Recarregue a página; se persistir,
      acione a TI.
    </p>
  );
  const erroDoSoac = (
    <p className="erro-de-tela">
      Não foi possível carregar o consumo de IA. Recarregue a página; se o problema persistir,
      acione a TI.
    </p>
  );

  return (
    <div
      className="Frame1000004012"
      style={{ height: 'fit-content', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
    >
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Consumo de IA
            <button type="button" className="help-circle-btn" title="Como Funciona" onClick={() => setIsHelpOpen(true)}>
              ?
            </button>
          </div>
        </div>

        <p className="admin-hint">
          Quanto o sistema está gastando com inteligência artificial, quem usou, para quê e quando. O
          custo em dólares é <strong>estimado</strong> pela tabela pública de preços — o número
          oficial é o do Console da Anthropic.
        </p>

        <div className="recepcao-tabs">
          <button type="button" className={`recepcao-tab ${aba === 'TOTAL' ? 'active' : ''}`} onClick={() => setAba('TOTAL')}>
            Total
          </button>
          <button type="button" className={`recepcao-tab ${aba === 'RADAR' ? 'active' : ''}`} onClick={() => setAba('RADAR')}>
            Radar e Assistente
          </button>
          {ORCAMENTO_CILIA_HABILITADO && (
            <button type="button" className={`recepcao-tab ${aba === 'COTACAO' ? 'active' : ''}`} onClick={() => setAba('COTACAO')}>
              Cotação online
            </button>
          )}
        </div>

        {carregando && <FullScreenLoader />}

        {!carregando && aba === 'TOTAL' && (
          erroSoac || erroCotacao ? (
            <>
              {erroSoac && erroDoSoac}
              {erroCotacao && erroDaCotacao}
              <p className="admin-texto-secundario">
                O total não é exibido enquanto uma das aplicações não responder, porque ficaria menor
                que o real. A aba da aplicação que respondeu continua disponível.
              </p>
            </>
          ) : (
            resumo && <AbaTotal soac={resumo} cotacao={ORCAMENTO_CILIA_HABILITADO ? cotacao : null} />
          )
        )}

        {!carregando && aba === 'RADAR' && (erroSoac ? erroDoSoac : resumo && <AbaRadarAssistente resumo={resumo} />)}

        {!carregando && aba === 'COTACAO' &&
          (erroCotacao ? erroDaCotacao : cotacao && <AbaCotacaoOnline resumo={cotacao} estado={estadoCotacao} />)}
      </div>

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Consumo de IA"
        secoes={helpSections}
      />
    </div>
  );
};

export default ConsumoIaPage;
